import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Download, Loader2, AlertTriangle, ChevronDown, ChevronRight, ExternalLink, Hash,
} from "lucide-react";
import {
  serpAnalysisApi,
  type HTagsCompetitor,
  type HTagsTopic,
  type HTagsAiTopic,
  type HeadingItem,
} from "@/api/serp-analysis";

// ── H-level badge colors — high contrast, dark-mode safe ──

const H_BADGE_COLORS: Record<number, string> = {
  1: "bg-emerald-600",
  2: "bg-sky-600",
  3: "bg-amber-600",
  4: "bg-violet-600",
  5: "bg-rose-600",
  6: "bg-zinc-500",
};

function getBadgeColor(level: number) {
  return H_BADGE_COLORS[level] || H_BADGE_COLORS[6];
}

// ── Normalize for cross-site matching ──

function normalize(text: string): string {
  return text.toLowerCase().replace(/[^\w\s\u4e00-\u9fff]/g, "").replace(/\s+/g, " ").trim();
}

// ── Overview: single heading line (compact, for grid view) ──

function HeadingLineCompact({
  heading,
  isCommon,
}: {
  heading: HeadingItem;
  isCommon: boolean;
}) {
  return (
    <div
      className={`flex items-start gap-1.5 py-0.5 text-xs ${
        isCommon ? "bg-amber-500/15 rounded px-1 -mx-1" : ""
      }`}
    >
      <span
        className={`${getBadgeColor(heading.level)} text-white text-[9px] font-bold px-1 py-0 rounded min-w-[22px] text-center shrink-0 mt-0.5`}
      >
        H{heading.level}
      </span>
      <span className={`flex-1 leading-tight ${isCommon ? "text-amber-300 dark:text-amber-300 font-medium" : "text-foreground/80"}`}>
        {heading.text}
      </span>
    </div>
  );
}

// ── Overview: site column (for grid layout like screenshot) ──

function SiteColumn({
  competitor,
  commonTopics,
}: {
  competitor: HTagsCompetitor;
  commonTopics: Map<string, number>;
}) {
  const [expanded, setExpanded] = useState(true);

  // Count headings by level
  const levelCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    for (const h of competitor.headings) {
      counts[h.level] = (counts[h.level] || 0) + 1;
    }
    return counts;
  }, [competitor.headings]);

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      {/* Site header */}
      <div
        className="px-3 py-2 border-b border-border bg-muted/30 cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <span className="text-primary font-bold text-sm">#{competitor.rank}</span>
          <span className="text-xs text-foreground font-medium truncate flex-1">
            {competitor.title || competitor.domain}
          </span>
          <a
            href={competitor.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-primary shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3 w-3" />
          </a>
          {expanded ? (
            <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
          )}
        </div>
        <div className="text-[10px] text-muted-foreground truncate mt-0.5">
          {competitor.domain}
        </div>
        {/* H-level counts */}
        <div className="flex gap-1 mt-1">
          {[1, 2, 3, 4, 5, 6].map((level) =>
            levelCounts[level] ? (
              <span
                key={level}
                className={`${getBadgeColor(level)} text-white text-[9px] font-bold px-1 rounded`}
              >
                H{level}:{levelCounts[level]}
              </span>
            ) : null
          )}
        </div>
      </div>

      {/* Headings list */}
      {expanded && (
        <div className="px-2 py-1.5 space-y-0.5 max-h-[500px] overflow-y-auto">
          {competitor.headings.map((h, i) => {
            const norm = normalize(h.text);
            const count = commonTopics.get(norm) || 0;
            return (
              <HeadingLineCompact
                key={`${h.level}-${i}`}
                heading={h}
                isCommon={count >= 2}
              />
            );
          })}
          {competitor.headings.length === 0 && (
            <p className="text-[10px] text-muted-foreground py-2 text-center">No headings</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Detail view: single heading line (full width, with counts) ──

function HeadingLineDetail({
  heading,
  isCommon,
  commonCount,
  totalSites,
}: {
  heading: HeadingItem;
  isCommon: boolean;
  commonCount: number;
  totalSites: number;
}) {
  const indent = (heading.level - 1) * 20;

  return (
    <div
      className={`flex items-center gap-2 py-1.5 px-3 rounded-md text-sm ${
        isCommon
          ? "bg-amber-500/10 dark:bg-amber-500/15 border-l-2 border-l-amber-500"
          : "border-l-2 border-l-transparent hover:bg-muted/50"
      }`}
      style={{ marginLeft: indent }}
    >
      <span
        className={`${getBadgeColor(heading.level)} text-white text-[10px] font-bold px-1.5 py-0.5 rounded min-w-[28px] text-center shrink-0`}
      >
        H{heading.level}
      </span>
      <span className="flex-1 text-foreground">{heading.text}</span>
      {isCommon && (
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 dark:text-amber-300 shrink-0">
          {commonCount}/{totalSites}
        </span>
      )}
    </div>
  );
}

// ── Detail view: competitor card (collapsible) ──

function CompetitorCard({
  competitor,
  commonTopics,
  totalSites,
}: {
  competitor: HTagsCompetitor;
  commonTopics: Map<string, number>;
  totalSites: number;
}) {
  const [open, setOpen] = useState(false);

  const levelCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    for (const h of competitor.headings) {
      counts[h.level] = (counts[h.level] || 0) + 1;
    }
    return counts;
  }, [competitor.headings]);

  const contentHeadings = useMemo(() => {
    const noise = /^(footer|navigation|menu|subscribe|newsletter|follow us|contact|sign up|log in|cart|account|my account|shopping cart|related|more results|skip to)/i;
    return competitor.headings.filter((h) => !noise.test(h.text.trim()));
  }, [competitor.headings]);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="overflow-hidden">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 py-3 px-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary font-bold text-lg shrink-0">
                #{competitor.rank}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-foreground truncate">
                  {competitor.title || competitor.domain}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {competitor.domain}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                {[1, 2, 3, 4, 5, 6].map((level) =>
                  levelCounts[level] ? (
                    <span
                      key={level}
                      className={`${getBadgeColor(level)} text-white text-[10px] font-bold px-1.5 py-0.5 rounded`}
                    >
                      H{level}:{levelCounts[level]}
                    </span>
                  ) : null
                )}
              </div>
              <a
                href={competitor.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-4 w-4" />
              </a>
              {open ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 pb-3 px-4 space-y-1">
            {contentHeadings.map((h, i) => {
              const norm = normalize(h.text);
              const count = commonTopics.get(norm) || 0;
              return (
                <HeadingLineDetail
                  key={`${h.level}-${i}`}
                  heading={h}
                  isCommon={count >= 2}
                  commonCount={count}
                  totalSites={totalSites}
                />
              );
            })}
            {contentHeadings.length === 0 && (
              <p className="text-sm text-muted-foreground py-2">No content headings found</p>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// ── AI Topic Summary (calls htags-topics endpoint, cached) ──

function AiTopicSummary({ requestId }: { requestId: string }) {
  const [showBelow40, setShowBelow40] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["analysis-htags-topics", requestId],
    queryFn: async () => {
      const res = await serpAnalysisApi.getHtagsTopics(requestId);
      return res.data;
    },
  });

  if (isLoading) {
    return (
      <div className="py-8 text-center">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        <p className="text-muted-foreground mt-2 text-sm">AI 正在分析主題分群...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center text-red-500">
        <AlertTriangle className="h-6 w-6 mx-auto mb-2" />
        <p className="text-sm">{(error as Error).message}</p>
      </div>
    );
  }

  if (!data?.topics || !Array.isArray(data.topics) || data.topics.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        <p className="text-sm">無法產出主題分群</p>
      </div>
    );
  }

  const topics = data.topics as HTagsAiTopic[];
  const mustWrite = topics.filter((t) => t.percentage >= 40);
  const optional = topics.filter((t) => t.percentage < 40);

  return (
    <div className="space-y-4">
      {/* Must-write topics (40%+) */}
      {mustWrite.length > 0 && (
        <div className="space-y-2">
          {mustWrite.map((topic, i) => (
            <AiTopicRow key={i} topic={topic} index={i + 1} />
          ))}
        </div>
      )}

      {mustWrite.length === 0 && (
        <p className="text-sm text-muted-foreground py-4">沒有超過 40% 覆蓋率的主題</p>
      )}

      {/* Below 40% — collapsible */}
      {optional.length > 0 && (
        <Collapsible open={showBelow40} onOpenChange={setShowBelow40}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="text-muted-foreground">
              {showBelow40 ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronRight className="h-3 w-3 mr-1" />}
              其他主題（{"<"}40%）— {optional.length} 個
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-2 mt-2">
              {optional.map((topic, i) => (
                <AiTopicRow key={i} topic={topic} index={mustWrite.length + i + 1} />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Meta info */}
      <div className="text-[10px] text-muted-foreground border-t pt-2 flex gap-4">
        <span>AI Model: {data.ai_model}</span>
        <span>Cost: {data.cost?.toFixed(4) || "0"} credits</span>
        {data.cached && <Badge variant="outline" className="text-[9px] py-0 px-1">cached</Badge>}
      </div>
    </div>
  );
}

function AiTopicRow({ topic, index }: { topic: HTagsAiTopic; index: number }) {
  const [showExamples, setShowExamples] = useState(false);
  const pct = topic.percentage;
  const barColor = pct >= 60 ? "bg-rose-500" : pct >= 40 ? "bg-amber-500" : "bg-sky-500";
  const barTrack = pct >= 60 ? "bg-rose-500/15" : pct >= 40 ? "bg-amber-500/15" : "bg-sky-500/15";

  return (
    <div className="rounded-lg border border-border bg-card hover:bg-muted/30 transition-colors">
      <div
        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
        onClick={() => setShowExamples(!showExamples)}
      >
        {/* Index */}
        <span className="text-muted-foreground font-mono text-xs w-5 text-right shrink-0">
          {index}.
        </span>

        {/* H level badge */}
        <span
          className={`${getBadgeColor(topic.suggested_h_level)} text-white text-[10px] font-bold px-1.5 py-0.5 rounded min-w-[28px] text-center shrink-0`}
        >
          H{topic.suggested_h_level}
        </span>

        {/* Topic name */}
        <div className="flex-1 min-w-0">
          <span className="text-sm text-foreground font-medium">{topic.topic}</span>
        </div>

        {/* Bar + percentage */}
        <div className="w-32 shrink-0">
          <div className="flex items-center gap-1.5">
            <div className={`flex-1 h-3 ${barTrack} rounded-full overflow-hidden`}>
              <div
                className={`h-full ${barColor} rounded-full transition-all`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs font-mono font-bold w-10 text-right text-foreground">
              {pct}%
            </span>
          </div>
        </div>

        {/* Count */}
        <span className="text-xs font-mono text-muted-foreground shrink-0">
          {topic.site_count} 站
        </span>

        {/* Expand */}
        {showExamples ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
        )}
      </div>

      {/* Example headings */}
      {showExamples && topic.example_headings?.length > 0 && (
        <div className="px-4 pb-2.5 pt-0 border-t border-border/50">
          <p className="text-[10px] text-muted-foreground mb-1">來源 H 標籤：</p>
          <div className="space-y-0.5">
            {topic.example_headings.map((h, i) => (
              <div key={i} className="text-xs text-foreground/70 pl-2 border-l border-muted">
                {h}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main HTagsTab ──

type ViewMode = "overview" | "detail" | "topics";

export default function HTagsTab({ requestId }: { requestId: string }) {
  const [view, setView] = useState<ViewMode>("overview");
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await serpAnalysisApi.exportHtagsExcel(requestId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Htags_${requestId.slice(0, 8)}_complete.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("完整已持久化 H 標籤 Excel 已下載");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "完整已持久化 H 標籤 Excel 下載失敗");
    } finally {
      setIsExporting(false);
    }
  };

  const exportButton = (
    <Button type="button" size="sm" variant="outline" onClick={handleExport} disabled={isExporting}>
      {isExporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
      {isExporting ? "匯出中..." : "下載完整已持久化 H 標籤 Excel"}
    </Button>
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ["analysis-htags", requestId],
    queryFn: async () => {
      const res = await serpAnalysisApi.getHtags(requestId);
      return res.data;
    },
  });

  // Build common topic map for highlighting
  const commonTopics = useMemo(() => {
    const map = new Map<string, number>();
    if (!data?.topics) return map;
    for (const topic of data.topics) {
      map.set(topic.normalized, topic.site_count);
      for (const v of topic.variants) {
        map.set(normalize(v), topic.site_count);
      }
    }
    return map;
  }, [data]);

  if (isLoading) {
    return (
      <div className="py-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        <p className="text-muted-foreground mt-2">Loading H-tag data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center text-red-500">
        <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
        <p>{(error as Error).message}</p>
      </div>
    );
  }

  if (!data?.competitors?.length) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <Hash className="h-8 w-8 mx-auto mb-2" />
        <p>尚無 H 標籤資料</p>
        <div className="mt-4 flex justify-center">{exportButton}</div>
      </div>
    );
  }

  const totalHeadings = data.competitors.reduce((sum, c) => sum + c.headings.length, 0);

  return (
    <div className="space-y-4">
      {/* Stats bar */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <Badge variant="outline">{data.total_sites} 個網站</Badge>
        <Badge variant="outline">{totalHeadings} 個 H 標籤</Badge>
        <Badge variant="outline">{data.topics.length} 個共同主題</Badge>

        <div className="flex-1" />
        {exportButton}

        {/* View toggle — 3 modes */}
        <div className="flex gap-1">
          <Button
            size="sm"
            variant={view === "overview" ? "default" : "outline"}
            onClick={() => setView("overview")}
          >
            總覽 Overview
          </Button>
          <Button
            size="sm"
            variant={view === "topics" ? "default" : "outline"}
            onClick={() => setView("topics")}
          >
            主題歸納 Topics
          </Button>
          <Button
            size="sm"
            variant={view === "detail" ? "default" : "outline"}
            onClick={() => setView("detail")}
          >
            逐站檢視 Detail
          </Button>
        </div>
      </div>

      {/* ═══ Overview: multi-column grid (like screenshot) ═══ */}
      {view === "overview" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {data.competitors.map((comp) => (
            <SiteColumn
              key={comp.rank}
              competitor={comp}
              commonTopics={commonTopics}
            />
          ))}
        </div>
      )}

      {/* ═══ Topic summary view (AI-powered) ═══ */}
      {view === "topics" && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              建議撰寫主題 — AI 語意分群（前 {data.total_sites} 名）
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              AI 分析前 {data.total_sites} 名的 H 標籤，歸納出共同主題。40% 以上 = 必寫內容（結果已快取，不重複扣點）
            </p>
          </CardHeader>
          <CardContent>
            <AiTopicSummary requestId={requestId} />
          </CardContent>
        </Card>
      )}

      {/* ═══ Detail: per-competitor collapsible cards ═══ */}
      {view === "detail" && (
        <div className="space-y-2">
          {data.competitors.map((comp) => (
            <CompetitorCard
              key={comp.rank}
              competitor={comp}
              commonTopics={commonTopics}
              totalSites={data.total_sites}
            />
          ))}
        </div>
      )}
    </div>
  );
}
